import { useCallback, useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { Badge } from '@/components/shadcn/badge';
import { Button } from '@/components/shadcn/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/shadcn/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/shadcn/tabs';
import showToast from '@/components/ui/Toast';
import ConnectWhatsappModal from '@/components/community/whatsapp/ConnectWhatsappModal';
import WhatsappMessagesTab from '@/components/community/whatsapp/WhatsappMessagesTab';
import WhatsappSessionsTab from '@/components/community/whatsapp/WhatsappSessionsTab';
import whatsappCommunityService, {
  countActiveSessions,
} from '@/services/whatsappCommunityService';

function getDefaultDeviceName(): string {
  return localStorage.getItem('communityName')?.trim() || 'Komunitas';
}

const WhatsappNotificationPage = () => {
  const [activeCount, setActiveCount] = useState(0);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [connectOpen, setConnectOpen] = useState(false);
  const [sessionsRefreshKey, setSessionsRefreshKey] = useState(0);

  const refreshConnection = useCallback(async () => {
    setLoadingStatus(true);
    try {
      const sessions = await whatsappCommunityService.listSessions();
      setActiveCount(countActiveSessions(sessions));
    } catch (error) {
      setActiveCount(0);
      showToast.error(
        error instanceof Error ? error.message : 'Gagal memuat status WhatsApp'
      );
    } finally {
      setLoadingStatus(false);
    }
  }, []);

  useEffect(() => {
    void refreshConnection();
  }, [refreshConnection]);

  const isConnected = activeCount > 0;
  const badgeLabel = loadingStatus
    ? 'Memuat...'
    : isConnected
      ? activeCount > 1
        ? `Terhubung (${activeCount})`
        : 'Terhubung'
      : 'Belum terhubung';

  const handleConnected = () => {
    setSessionsRefreshKey((k) => k + 1);
    void refreshConnection();
  };

  return (
    <div className="mx-auto w-full max-w-6xl space-y-4 px-4 py-4 lg:space-y-6 lg:px-0 lg:py-0">
      <Card className="overflow-hidden rounded-2xl border-[#E0E6ED] shadow-sm">
        <CardHeader className="space-y-3 p-4 sm:p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <CardTitle className="text-base font-semibold text-[#0E1726] sm:text-xl">
                  WhatsApp Notification
                </CardTitle>
                <Badge
                  variant="secondary"
                  data-testid="wa-connection-badge"
                  className={
                    isConnected
                      ? 'border-transparent bg-green-100 text-green-800 hover:bg-green-100'
                      : 'border-transparent bg-gray-100 text-gray-600 hover:bg-gray-100'
                  }
                >
                  {badgeLabel}
                </Badge>
              </div>
              <CardDescription className="text-sm leading-relaxed text-[#556575]">
                Kirim info event, pengingat, dan status pembayaran ke WhatsApp member secara
                otomatis.
              </CardDescription>
            </div>
            <Button
              type="button"
              className="h-11 w-full shrink-0 rounded-xl bg-primary-500 text-white hover:bg-primary-600 lg:h-10 lg:w-auto"
              onClick={() => setConnectOpen(true)}
            >
              <Icon icon="mdi:link-variant" className="h-4 w-4" />
              Hubungkan WhatsApp
            </Button>
          </div>
        </CardHeader>

        {!isConnected && !loadingStatus && (
          <CardContent className="border-t border-dashed border-gray-100 p-4 pt-3 sm:px-6">
            <p
              className="rounded-xl bg-gray-50 px-3 py-2.5 text-sm text-[#556575]"
              data-testid="wa-empty-hint"
            >
              Belum ada nomor WhatsApp yang terhubung. Ketuk Hubungkan WhatsApp untuk mulai.
            </p>
          </CardContent>
        )}
      </Card>

      <Tabs defaultValue="messages" className="w-full">
        <TabsList
          variant="line"
          className="flex h-auto w-full justify-start gap-0 overflow-x-auto rounded-none border-b border-gray-200 bg-transparent p-0"
        >
          <TabsTrigger
            value="messages"
            variant="line"
            className="flex-1 gap-1.5 px-3 py-3 text-sm sm:flex-none sm:px-4"
          >
            <Icon icon="mdi:chat-outline" className="h-4 w-4" />
            Pesan
          </TabsTrigger>
          <TabsTrigger
            value="sessions"
            variant="line"
            className="flex-1 gap-1.5 px-3 py-3 text-sm sm:flex-none sm:px-4"
          >
            <Icon icon="mdi:cellphone-link" className="h-4 w-4" />
            Session
          </TabsTrigger>
        </TabsList>

        <TabsContent value="messages" className="mt-4 focus-visible:ring-0">
          <WhatsappMessagesTab />
        </TabsContent>
        <TabsContent value="sessions" className="mt-4 focus-visible:ring-0">
          <WhatsappSessionsTab
            refreshKey={sessionsRefreshKey}
            onSessionsChange={() => void refreshConnection()}
          />
        </TabsContent>
      </Tabs>

      <ConnectWhatsappModal
        isOpen={connectOpen}
        onClose={() => setConnectOpen(false)}
        defaultDeviceName={getDefaultDeviceName()}
        onConnected={handleConnected}
      />
    </div>
  );
};

export default WhatsappNotificationPage;
